import { useState, useCallback, useRef } from 'react';

const HINT_COST = 25;
const HINT_DURATION = 5000; // 5 seconds

/**
 * Hook to manage the hint feature
 * Highlights a random unplaced piece and its correct location on the board
 */
export function useHintFeature() {
  const [hintPieceId, setHintPieceId] = useState(null);
  const [hintTargetCell, setHintTargetCell] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const timeoutRef = useRef(null);
  
  // Calculate total hint penalty for score
  const hintPenalty = hintsUsed * HINT_COST;
  
  // Request a hint (shows confirmation first)
  const requestHint = useCallback(() => {
    setShowConfirmation(true);
  }, []);
  
  // Cancel hint request
  const cancelHint = useCallback(() => {
    setShowConfirmation(false);
  }, []);
  
  // Confirm and activate hint
  const confirmHint = useCallback((traySlots, board) => {
    setShowConfirmation(false);
    
    // Find all unplaced pieces in tray
    const unplacedPieces = traySlots.filter(p => p !== null);
    
    if (unplacedPieces.length === 0) {
      return false; // No pieces to hint
    }
    
    // Pick a random unplaced piece
    const randomIndex = Math.floor(Math.random() * unplacedPieces.length);
    const hintPiece = unplacedPieces[randomIndex];
    
    // Set hint state
    setHintPieceId(hintPiece.id);
    setHintTargetCell({ row: hintPiece.correctRow, col: hintPiece.correctCol });
    setHintsUsed(prev => prev + 1);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Auto-clear hint after duration
    timeoutRef.current = setTimeout(() => {
      setHintPieceId(null);
      setHintTargetCell(null);
    }, HINT_DURATION);
    
    return true;
  }, []);
  
  // Clear hint manually (e.g., when piece is placed)
  const clearHint = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHintPieceId(null);
    setHintTargetCell(null);
  }, []);
  
  // Reset hints (for new game)
  const resetHints = useCallback(() => {
    clearHint();
    setHintsUsed(0);
    setShowConfirmation(false);
  }, [clearHint]);
  
  // Check if a specific piece is the hint piece
  const isPieceHinted = useCallback((pieceId) => {
    return hintPieceId === pieceId;
  }, [hintPieceId]);
  
  // Check if a specific cell is the hint target
  const isCellHinted = useCallback((row, col) => {
    return hintTargetCell?.row === row && hintTargetCell?.col === col;
  }, [hintTargetCell]);
  
  return {
    // State
    hintPieceId,
    hintTargetCell,
    showConfirmation,
    hintsUsed,
    hintPenalty,
    hintCost: HINT_COST,
    isHintActive: hintPieceId !== null,
    
    // Actions
    requestHint,
    cancelHint,
    confirmHint,
    clearHint,
    resetHints,
    
    // Helpers
    isPieceHinted,
    isCellHinted
  };
}
